import { entries, fromPairs, isArray, isEmpty, isNil, isPlainObject, isString, uniq } from "lodash";
import type { QuotedModelIdFinder } from "../process-model-names";
import { createQuotedModelIdFinder, renameQuotedModelIds } from "../process-model-names";
import { ProcessModelCopyNodeType, ProcessModelCopyReferenceType } from "./process-model-copy.model";

// The serialized Blockly editor state of an expression, which is shown as the expression itself already
export const PROCESS_MODEL_BLOCKLY_WORKSPACE_SUFFIX = "BlocklyWorkspace";

// Fields of a task that refer to other tasks by their modelId
export const PROCESS_MODEL_STRUCTURAL_REFERENCE_KEYS: readonly string[] = [
  "predecessorsIds",
  "atLeastOnePredecessorIds",
  "reactivateTasksOnFinish",
  "reactivateTasksOnCancel",
  "attachmentTasks",
  "referenceTaskUploadModelIds",
  "lockDocumentsAfterReviewForIds",
  "recruitingConceptTaskId",
  "prefillTaskId",
  "interviewDateTaskId",
  "feedbackInterviewerTaskId",
  "feedbackIntervieweeTaskId",
];

// The targets of a rule, which name tasks, phases or areas by their modelId
export const PROCESS_MODEL_RULE_TARGET_REFERENCE_KEYS: readonly string[] = [
  "taskIds",
  "phaseIds",
  "areaIds",
  "excludeTaskIds",
];

// Expressions, which quote the modelIds of the tasks they refer to
export const PROCESS_MODEL_EXPRESSION_KEYS: readonly string[] = [
  "activationCondition",
  "condition",
  "runCondition",
  "expression",
];

/** A place where a copied element refers to other elements by their modelId. */
export interface ReferenceOccurrence {
  /** The path of the value in the element, e.g. `predecessorsIds` or `rules.0.targetTasks.taskIds`. */
  field: string;
  type: ProcessModelCopyReferenceType;
  referencedNodeType: ProcessModelCopyNodeType;
  referencedModelIds: string[];
  /** The text of an expression or Blockly workspace. */
  expression?: string;
}

/** A reference as {@link rewriteReferences} hands it to its resolver. */
export interface ReferenceToResolve {
  field: string;
  type: ProcessModelCopyReferenceType;
  referencedModelId: string;
}

/**
 * What a reference becomes: the modelId it points to from now on (the same one keeps it), or `null` to drop it from
 * a field. Expressions and Blockly workspaces cannot drop a reference, so `null` keeps it there.
 */
export type ReferenceResolver = (reference: ReferenceToResolve) => string | null;

interface ReferenceContext {
  field: string;
  type: ProcessModelCopyReferenceType;
}

/** Maps the value of a reference field: the new value, or `null` to drop the field. */
type ReferenceValueMapper = (value: string | string[], context: ReferenceContext) => string | string[] | null;

const RULE_TARGET_NODE_TYPES: Readonly<Record<string, ProcessModelCopyNodeType>> = {
  taskIds: ProcessModelCopyNodeType.Task,
  excludeTaskIds: ProcessModelCopyNodeType.Task,
  phaseIds: ProcessModelCopyNodeType.Phase,
  areaIds: ProcessModelCopyNodeType.Area,
};

// The task selector of a Blockly workspace, as Blockly serializes it
const BLOCKLY_TASK_FIELD = /(<field name="TASK_MODELID">)([^<]*)(<\/field>)/g;

// One finder per set of known ids: a plan looks for the same ids in every expression of every copied task. The sets are
// read-only, so a finder never misses an id added later
const quotedModelIdFinders = new WeakMap<ReadonlySet<string>, QuotedModelIdFinder>();

/**
 * Every reference an element (in practice a task) holds to other areas, phases or tasks: the id fields, rule
 * targets, expressions and Blockly workspaces. Fields list every id they hold; expressions and workspaces only the
 * known ids they quote, since any other quoted text (a role, a template) is no reference.
 */
export function findReferences(element: object, knownModelIds: ReadonlySet<string>): ReferenceOccurrence[] {
  const occurrences: ReferenceOccurrence[] = [];
  mapReferenceValues(element, (value, { field, type }) => {
    const referencedModelIds = getReferencedModelIds(value, type, knownModelIds);
    if (!isEmpty(referencedModelIds)) {
      const isText = [ProcessModelCopyReferenceType.Expression, ProcessModelCopyReferenceType.Blockly].includes(type);
      occurrences.push({
        field,
        type,
        referencedNodeType: getReferencedNodeType(field, type),
        referencedModelIds,
        ...(isText && isString(value) ? { expression: value } : {}),
      });
    }

    return value;
  });
  return occurrences;
}

/**
 * A copy of the element with every reference resolved: a field drops the ids the resolver drops, an expression and a
 * Blockly workspace get the new ids quoted where the old ones were. A field holding a single id is removed when its id
 * is dropped.
 */
export function rewriteReferences<T extends object>(
  element: T,
  knownModelIds: ReadonlySet<string>,
  resolve: ReferenceResolver
): T {
  return mapReferenceValues(element, (value, { field, type }) => {
    const resolveId = (referencedModelId: string): string | null => resolve({ field, type, referencedModelId });
    switch (type) {
      case ProcessModelCopyReferenceType.Field:
      case ProcessModelCopyReferenceType.RuleTarget:
        return rewriteIdField(value, resolveId);
      case ProcessModelCopyReferenceType.Expression:
        return isString(value) ? rewriteExpression(value, knownModelIds, resolveId) : value;
      case ProcessModelCopyReferenceType.Blockly:
        return isString(value) ? rewriteBlocklyWorkspace(value, knownModelIds, resolveId) : value;
      default: {
        const unhandled: never = type;
        throw new Error(`Unknown reference type ${unhandled}`);
      }
    }
  });
}

/** A copy of the element with every expression (activation conditions, rule and e-mail conditions, …) transformed. */
export function mapExpressions<T extends object>(
  element: T,
  transform: (expression: string, field: string) => string
): T {
  return mapReferenceValues(element, (value, { field, type }) =>
    type === ProcessModelCopyReferenceType.Expression && isString(value) ? transform(value, field) : value
  );
}

function getReferencedModelIds(
  value: string | string[],
  type: ProcessModelCopyReferenceType,
  knownModelIds: ReadonlySet<string>
): string[] {
  switch (type) {
    case ProcessModelCopyReferenceType.Field:
    case ProcessModelCopyReferenceType.RuleTarget:
      return uniq((isArray(value) ? value : [value]).filter((id) => isString(id) && !isEmpty(id)));
    case ProcessModelCopyReferenceType.Expression:
      return isString(value) ? getQuotedModelIdFinder(knownModelIds)(value) : [];
    case ProcessModelCopyReferenceType.Blockly:
      return isString(value) ? findBlocklyModelIds(value, knownModelIds) : [];
    default: {
      const unhandled: never = type;
      throw new Error(`Unknown reference type ${unhandled}`);
    }
  }
}

function getReferencedNodeType(field: string, type: ProcessModelCopyReferenceType): ProcessModelCopyNodeType {
  if (type !== ProcessModelCopyReferenceType.RuleTarget) {
    return ProcessModelCopyNodeType.Task;
  }

  const key = field.split(".").at(-1);
  return RULE_TARGET_NODE_TYPES[key] ?? ProcessModelCopyNodeType.Task;
}

function rewriteIdField(value: string | string[], resolveId: (id: string) => string | null): string | string[] | null {
  if (isArray(value)) {
    return uniq(value.map((id) => resolveId(id)).filter((id) => !isNil(id)));
  }

  return resolveId(value);
}

function rewriteExpression(
  expression: string,
  knownModelIds: ReadonlySet<string>,
  resolveId: (id: string) => string | null
): string {
  const newIdsByOldId = new Map(
    getQuotedModelIdFinder(knownModelIds)(expression)
      .map((id): [string, string] => [id, resolveId(id) ?? id])
      .filter(([id, newId]) => id !== newId)
  );
  return renameQuotedModelIds(expression, newIdsByOldId);
}

function getQuotedModelIdFinder(knownModelIds: ReadonlySet<string>): QuotedModelIdFinder {
  const cached = quotedModelIdFinders.get(knownModelIds);
  if (!isNil(cached)) {
    return cached;
  }

  const finder = createQuotedModelIdFinder(knownModelIds);
  quotedModelIdFinders.set(knownModelIds, finder);
  return finder;
}

function findBlocklyModelIds(workspace: string, knownModelIds: ReadonlySet<string>): string[] {
  return uniq(
    Array.from(workspace.matchAll(BLOCKLY_TASK_FIELD), (match) => match[2]).filter((id) => knownModelIds.has(id))
  );
}

function rewriteBlocklyWorkspace(
  workspace: string,
  knownModelIds: ReadonlySet<string>,
  resolveId: (id: string) => string | null
): string {
  return workspace.replace(BLOCKLY_TASK_FIELD, (field: string, open: string, id: string, close: string) =>
    knownModelIds.has(id) ? `${open}${resolveId(id) ?? id}${close}` : field
  );
}

function getReferenceType(key: string, value: unknown): ProcessModelCopyReferenceType | null {
  const isIdValue = isString(value) || (isArray(value) && value.every((item) => isString(item)));
  if (PROCESS_MODEL_STRUCTURAL_REFERENCE_KEYS.includes(key) && isIdValue) {
    return ProcessModelCopyReferenceType.Field;
  }

  if (PROCESS_MODEL_RULE_TARGET_REFERENCE_KEYS.includes(key) && isIdValue) {
    return ProcessModelCopyReferenceType.RuleTarget;
  }

  if (PROCESS_MODEL_EXPRESSION_KEYS.includes(key) && isString(value)) {
    return ProcessModelCopyReferenceType.Expression;
  }

  if (key.endsWith(PROCESS_MODEL_BLOCKLY_WORKSPACE_SUFFIX) && isString(value)) {
    return ProcessModelCopyReferenceType.Blockly;
  }

  return null;
}

/**
 * Walks plain objects and arrays only: values such as dates are class instances and stay as they are, and nothing a
 * reference could hide in is one.
 */
function mapReferenceValues<T>(value: T, mapper: ReferenceValueMapper, path = ""): T {
  if (isArray(value)) {
    return <T>(<unknown>value.map((item, index) => mapReferenceValues(item, mapper, joinPath(path, String(index)))));
  }

  if (!isPlainRecord(value)) {
    return value;
  }

  const mappedEntries = entries(value).flatMap(([key, child]): [string, unknown][] => {
    const field = joinPath(path, key);
    const type = getReferenceType(key, child);
    if (isNil(type)) {
      return [[key, mapReferenceValues(child, mapper, field)]];
    }

    const mapped = mapper(<string | string[]>child, { field, type });
    return isNil(mapped) ? [] : [[key, mapped]];
  });
  return <T>fromPairs(mappedEntries);
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value);
}

function joinPath(path: string, key: string): string {
  return isEmpty(path) ? key : `${path}.${key}`;
}
